import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Create configured axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor to include auth token in all requests
api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

// Configure axios to handle blob errors properly - prevent XMLHttpRequest responseText errors
api.interceptors.response.use(
  response => response.data,
  error => {
    // Handle 401 Unauthorized - redirect to login
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
      return Promise.reject(new Error('Session expired. Please log in again.'));
    }
    
    // Suppress errors for blob responses to prevent "Failed to read responseText" errors
    if (error.config && error.config.responseType === 'blob') {
      // For blob responses, don't try to read error details - just return a clean error
      const status = error.response?.status || 500;
      return Promise.reject(new Error(`Request failed with status ${status}`));
    }
    return Promise.reject(error);
  }
);

// Export the configured api instance for direct use
export { api };

// Accounts
export const getAccounts = () => axios.get(`${API}/accounts`);
export const getAccount = (id) => axios.get(`${API}/accounts/${id}`);
export const createAccount = (data) => axios.post(`${API}/accounts`, data);
export const updateAccount = (id, data) => axios.put(`${API}/accounts/${id}`, data);
export const deleteAccount = (id) => axios.delete(`${API}/accounts/${id}`);
export const importChartOfAccounts = (formData) => axios.post(`${API}/accounts/import`, formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});

// Templates
export const getTemplates = () => axios.get(`${API}/templates`);
export const getTemplate = (id) => axios.get(`${API}/templates/${id}`);
export const createTemplate = (data) => axios.post(`${API}/templates`, data);
export const deleteTemplate = (id) => axios.delete(`${API}/templates/${id}`);

// Transactions
export const getTransactions = (params) => axios.get(`${API}/transactions`, { params });
export const createTransaction = (data) => axios.post(`${API}/transactions`, data);
export const importTransactions = (formData) => axios.post(`${API}/transactions/import`, formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const matchTransactions = (transactionIds) => axios.post(`${API}/transactions/match`, { transaction_ids: transactionIds });
export const unmatchTransactions = (transactionIds) => axios.post(`${API}/transactions/unmatch`, { transaction_ids: transactionIds });
export const autoMatchTransactions = (accountId) => axios.post(`${API}/transactions/auto-match/${accountId}`);
export const deleteTransaction = (id) => axios.delete(`${API}/transactions/${id}`);
export const bulkDeleteTransactions = (transactionIds) => axios.post(`${API}/transactions/bulk-delete`, transactionIds);

// Reconciliations
export const getReconciliations = (params) => axios.get(`${API}/reconciliations`, { params });
export const getReconciliation = (id) => axios.get(`${API}/reconciliations/${id}`);
export const createReconciliation = (data) => axios.post(`${API}/reconciliations`, data);
export const prepareReconciliation = (id) => axios.post(`${API}/reconciliations/${id}/prepare`);
export const reviewReconciliation = (id) => axios.post(`${API}/reconciliations/${id}/review`);
export const recalculateReconciliation = (id) => axios.post(`${API}/reconciliations/${id}/recalculate`);

// Dashboard
export const getDashboardSummary = () => axios.get(`${API}/dashboard/summary`);
export const getBalanceTrends = (accountId) => axios.get(`${API}/dashboard/balance-trends`, { params: { account_id: accountId } });

// Work Papers
export const getWorkPapers = (params) => axios.get(`${API}/workpapers`, { params });
export const createWorkPaper = (data) => axios.post(`${API}/workpapers`, data);
export const prepareWorkPaper = (id) => axios.post(`${API}/workpapers/${id}/prepare`);
export const reviewWorkPaper = (id) => axios.post(`${API}/workpapers/${id}/review`);

// Exports
export const exportReconciliationExcel = (reconId) => {
  return axios.get(`${API}/export/reconciliation/${reconId}/excel`, { responseType: 'blob' });
};

export const exportReconciliationPDF = (reconId) => {
  return axios.get(`${API}/export/reconciliation/${reconId}/pdf`, { responseType: 'blob' });
};
export const exportTransactionsExcel = (accountId, startDate, endDate) => {
  return axios.get(`${API}/export/transactions/${accountId}/excel`, {
    params: { start_date: startDate, end_date: endDate },
    responseType: 'blob'
  });
};

// Attachments

export const bulkUserAction = (action, userIds) => 
  axios.post(`${API}/users/bulk-action`, null, {
    params: {
      action,
      user_ids: userIds
    }
  });

export const uploadAttachment = (transactionId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return axios.post(`${API}/transactions/${transactionId}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const getTransactionAttachments = (transactionId) => axios.get(`${API}/transactions/${transactionId}/attachments`);
export const downloadAttachment = (attachmentId) => {
  return axios.get(`${API}/attachments/${attachmentId}/download`, { 
    responseType: 'blob',
    validateStatus: (status) => status === 200
  });
};
export const deleteAttachment = (attachmentId) => axios.delete(`${API}/attachments/${attachmentId}`);

// Entity
export const getEntity = () => axios.get(`${API}/entity`);
export const updateEntity = (data) => axios.put(`${API}/entity`, data);

// User Management (Admin)
export const getUsers = () => axios.get(`${API}/users`);
export const createUser = (data) => axios.post(`${API}/users`, data);
export const updateUser = (id, data) => axios.put(`${API}/users/${id}`, data);
export const deleteUser = (id) => axios.delete(`${API}/users/${id}`);
export const resetUserPassword = (id) => axios.post(`${API}/users/${id}/reset-password`);
export const changePassword = (data) => axios.post(`${API}/auth/change-password`, data);

// Notifications
export const getNotifications = (params) => axios.get(`${API}/notifications`, { params });
export const getUnreadCount = () => axios.get(`${API}/notifications/unread-count`);
export const markNotificationRead = (id) => axios.post(`${API}/notifications/${id}/read`);
export const markAllNotificationsRead = () => axios.post(`${API}/notifications/read-all`);
export const deleteNotification = (id) => axios.delete(`${API}/notifications/${id}`);

// Audit Logs (Admin)
export const getAuditLogs = (params) => axios.get(`${API}/audit-logs`, { params });

// User Preferences
export const getUserPreferences = () => axios.get(`${API}/preferences`);
export const updateUserPreferences = (data) => axios.put(`${API}/preferences`, data);
export const getAvailableColorSchemes = () => axios.get(`${API}/preferences/color-schemes`);

// Global Search
export const globalSearch = (query) => axios.get(`${API}/search`, { params: { q: query } });

// Period Analytics
export const getPeriodAnalytics = (accountId, periodType) => 
  axios.get(`${API}/period-analytics`, { params: { account_id: accountId, period_type: periodType } });

// Reconciliation Operations
export const updateReconciliation = (reconId, data) => axios.put(`${API}/reconciliations/${reconId}`, data);
export const deleteReconciliation = (reconId) => axios.delete(`${API}/reconciliations/${reconId}`);

// Bulk Transaction Operations
export const bulkUpdateTransactions = (data) => axios.post(`${API}/transactions/bulk-update`, data);
export const bulkAttachFile = (transactionIds, file) => {
  const formData = new FormData();
  formData.append('transaction_ids', JSON.stringify(transactionIds));
  formData.append('file', file);
  return axios.post(`${API}/transactions/bulk-attach`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Entity Branding
export const uploadEntityLogo = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return axios.post(`${API}/entity/logo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const getEntityLogo = async () => {
  // Use Fetch API instead of axios to avoid XMLHttpRequest responseText errors with blob responses
  const token = localStorage.getItem('token');
  const response = await fetch(`${API}/entity/logo`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    throw new Error('Logo not found');
  }
  
  const blob = await response.blob();
  return { data: blob };
};

// Entity Management
export const listUserEntities = () => axios.get(`${API}/entities/list`);
export const switchEntity = (entityId) => axios.post(`${API}/entities/switch`, { entity_id: entityId });

// Template Download
export const downloadTemplate = (templateId) => {
  return axios.get(`${API}/templates/${templateId}/download`, { 
    responseType: 'blob',
    validateStatus: (status) => status === 200
  });
};
export const updateTemplate = (templateId, data) => axios.put(`${API}/templates/${templateId}`, data);

// Report Templates
export const getReportTemplates = (scope) => axios.get(`${API}/report-templates`, { params: { scope } });
export const createReportTemplate = (data) => axios.post(`${API}/report-templates`, data);
export const deleteReportTemplate = (templateId) => axios.delete(`${API}/report-templates/${templateId}`);

// Advanced Analytics
export const getComprehensiveAnalytics = (periodType, accountId, customStart, customEnd) => {
  const params = { period_type: periodType };
  if (accountId) params.account_id = accountId;
  if (customStart) params.custom_start = customStart;
  if (customEnd) params.custom_end = customEnd;
  return axios.get(`${API}/analytics/comprehensive`, { params });
};

export const generateAIInsights = (data) => axios.post(`${API}/analytics/insights`, data);
export const getTransactionDrilldown = (accountId, startDate, endDate) => 
  axios.get(`${API}/analytics/drill-down`, { params: { account_id: accountId, start_date: startDate, end_date: endDate } });

// Export Analytics, MD&A, and FP&A reports
export const exportAnalyticsPDF = (analyticsData) => 
  axios.post(`${API}/analytics/export-pdf`, analyticsData, { responseType: 'blob' });
export const exportAnalyticsExcel = (analyticsData) => 
  axios.post(`${API}/analytics/export-excel`, analyticsData, { responseType: 'blob' });

export const exportMDAPDF = (mdaData) => 
  axios.post(`${API}/mda/export-pdf`, mdaData, { responseType: 'blob' });
export const exportMDAExcel = (mdaData) => 
  axios.post(`${API}/mda/export-excel`, mdaData, { responseType: 'blob' });

export const exportFPAPDF = (fpaData) => 
  axios.post(`${API}/fpa/export-pdf`, fpaData, { responseType: 'blob' });
export const exportFPAExcel = (fpaData) => 
  axios.post(`${API}/fpa/export-excel`, fpaData, { responseType: 'blob' });

// Advanced Analytics
export const getFinancialRatios = (period) => 
  axios.get(`${API}/advanced-analytics/financial-ratios`, { params: { period } });
export const getVarianceAnalysis = (currentPeriod, comparisonPeriod) => 
  axios.get(`${API}/advanced-analytics/variance-analysis`, { 
    params: { current_period: currentPeriod, comparison_period: comparisonPeriod } 
  });
export const getStatisticalAnalysis = (accountNumber, periods) => 
  axios.get(`${API}/advanced-analytics/statistical-analysis`, { 
    params: { account_number: accountNumber, periods } 
  });
export const getOperationalKPIs = (periodStart, periodEnd) => 
  axios.get(`${API}/advanced-analytics/operational-kpis`, { 
    params: { period_start: periodStart, period_end: periodEnd } 
  });

// Enhanced MD&A
export const generateExecutiveSummary = (data) =>
  axios.post(`${API}/mda-enhanced/generate-executive-summary`, data);
export const analyzeFluctuations = (data) =>
  axios.post(`${API}/mda-enhanced/analyze-fluctuations`, data);

// FP&A Scenario Planning
export const getAssumptionTemplates = () =>
  axios.get(`${API}/fpa-scenarios/assumption-templates`);
export const generateScenario = (data) =>
  axios.post(`${API}/fpa-scenarios/generate`, data);
export const compareScenarios = (data) =>
  axios.post(`${API}/fpa-scenarios/compare-scenarios`, data);
export const runSensitivityAnalysis = (data) =>
  axios.post(`${API}/fpa-scenarios/sensitivity-analysis`, data);

// FP&A Scenario Export APIs
export const exportScenarioCSV = (scenarioData) =>
  axios.post(`${API}/fpa-scenarios/export/csv`, scenarioData, { responseType: 'blob' });
export const exportScenarioJSON = (scenarioData) =>
  axios.post(`${API}/fpa-scenarios/export/json`, scenarioData, { responseType: 'blob' });
export const exportComparisonCSV = (comparisonData) =>
  axios.post(`${API}/fpa-scenarios/comparison/export/csv`, comparisonData, { responseType: 'blob' });

// Outstanding Items
export const getOutstandingItems = (reconciliationId, params) => 
  axios.get(`${API}/outstanding-items/reconciliation/${reconciliationId}`, { params });

// Entity Management - Multi-Tenancy
export const getMyEntities = () => axios.get(`${API}/entities/my-entities`);
export const addUserToEntity = (entityId, userEmail, role) => 
  axios.post(`${API}/entities/${entityId}/add-user`, null, { params: { user_email: userEmail, role } });
export const removeUserFromEntity = (entityId, userId) =>
  axios.delete(`${API}/entities/${entityId}/remove-user/${userId}`);
export const getEntityMembers = (entityId) => axios.get(`${API}/entities/${entityId}/members`);

// Cross-Entity Reporting
export const getCrossEntityTrialBalance = (entityIds, asOfDate) =>
  axios.get(`${API}/cross-entity/trial-balance`, { params: { entity_ids: entityIds, as_of_date: asOfDate } });
export const getCrossEntityBalanceSheet = (entityIds, asOfDate) =>
  axios.get(`${API}/cross-entity/balance-sheet`, { params: { entity_ids: entityIds, as_of_date: asOfDate } });
export const getCrossEntityIncomeStatement = (entityIds, startDate, endDate) =>
  axios.get(`${API}/cross-entity/income-statement`, { params: { entity_ids: entityIds, start_date: startDate, end_date: endDate } });
export const getEntitiesSummary = (entityIds = 'all') =>
  axios.get(`${API}/cross-entity/summary`, { params: { entity_ids: entityIds } });
export const createOutstandingItem = (data) => 
  axios.post(`${API}/outstanding-items`, data);
export const updateOutstandingItem = (itemId, data) => 
  axios.put(`${API}/outstanding-items/${itemId}`, data);
export const clearOutstandingItem = (itemId, data) => 
  axios.post(`${API}/outstanding-items/${itemId}/clear`, data);
export const unclearOutstandingItem = (itemId) => 
  axios.post(`${API}/outstanding-items/${itemId}/unclear`);
export const deleteOutstandingItem = (itemId) => 
  axios.delete(`${API}/outstanding-items/${itemId}`);
export const getAgingReport = (accountId) => 
  axios.get(`${API}/outstanding-items/account/${accountId}/aging-report`);
export const autoIdentifyOutstandingItems = (reconciliationId) => 
  axios.post(`${API}/outstanding-items/reconciliation/${reconciliationId}/auto-identify`);

// Trial Balance
export const importTrialBalance = (periodIdentifier, openingDate, closingDate, file, isOpeningBalance = false) => {
  const formData = new FormData();
  formData.append('file', file);
  return axios.post(`${API}/trial-balance/import?period_identifier=${periodIdentifier}&opening_date=${openingDate}&closing_date=${closingDate}&is_opening_balance=${isOpeningBalance}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const getTrialBalancePeriods = () => axios.get(`${API}/trial-balance/periods`);
export const getTrialBalanceForPeriod = (period) => axios.get(`${API}/trial-balance/period/${period}`);
export const updateTrialBalanceEntry = (tbId, data) => axios.put(`${API}/trial-balance/${tbId}`, data);
export const deleteTrialBalanceEntry = (tbId) => axios.delete(`${API}/trial-balance/${tbId}`);
export const deleteTrialBalancePeriod = (period) => axios.delete(`${API}/trial-balance/period/${period}`);
export const exportTrialBalance = (period) => 
  axios.get(`${API}/trial-balance/export/${period}`, { responseType: 'blob' });

// Get totals for Trial Balance (with date range and account filter)
export const getTrialBalanceTotals = (params) => axios.get(`${API}/trial-balance/totals`, { params });

// Get totals for General Ledger (with date range and account filter)
export const getGLTotals = (params) => axios.get(`${API}/gl/totals`, { params });

// Get GL transactions (with filtering by account, date range, etc.)
export const getGLTransactions = (params) => axios.get(`${API}/gl/transactions`, { params });

// Get Trial Balance entries for a period
export const getTrialBalanceEntries = (params) => axios.get(`${API}/trial-balance/period/${params.period}`);

// Custom Financial Statement Views
export const getFinancialStatementViews = (statementType) => 
  axios.get(`${API}/financial-statements/views`, { params: { statement_type: statementType } });

export const saveFinancialStatementView = (viewData) => 
  axios.post(`${API}/financial-statements/views`, viewData);

export const updateFinancialStatementView = (viewId, viewData) => 
  axios.put(`${API}/financial-statements/views/${viewId}`, viewData);

export const deleteFinancialStatementView = (viewId) => 
  axios.delete(`${API}/financial-statements/views/${viewId}`);

// Account Management

// User Permissions
export const updateUserPermissions = (userId, permissions) => axios.put(`${API}/users/${userId}/permissions`, permissions);


// Profile Photo
export const uploadProfilePhoto = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return axios.post(`${API}/users/profile-photo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const getProfilePhoto = () => axios.get(`${API}/users/profile-photo`);
export const deleteProfilePhoto = () => axios.delete(`${API}/users/profile-photo`);

// Financial Statements
export const getClassificationHierarchy = () => axios.get(`${API}/financial-statements/classification-hierarchy`);
export const autoClassifyAccounts = () => axios.post(`${API}/financial-statements/auto-classify`);
export const updateAccountClassification = (accountId, data) => axios.put(`${API}/financial-statements/accounts/${accountId}/classification`, null, { params: data });
export const bulkClassifyAccounts = (data) => axios.post(`${API}/financial-statements/accounts/bulk-classify`, data);
export const getBalanceSheet = (params) => axios.get(`${API}/financial-statements/balance-sheet`, { params });
export const getIncomeStatement = (params) => axios.get(`${API}/financial-statements/income-statement`, { params });
export const getMultiPeriodBalanceSheet = (periodIdentifiers, dataSource = 'tb') => axios.get(`${API}/financial-statements/balance-sheet/multi-period`, { 
  params: { 
    period_identifiers: periodIdentifiers.join(','),
    data_source: dataSource
  } 
});
export const exportBalanceSheet = (params) => axios.get(`${API}/financial-statements/balance-sheet/export`, { 
  params, 
  responseType: 'blob' 
});
export const exportBalanceSheetPDF = (params) => axios.get(`${API}/financial-statements/balance-sheet/export-pdf`, { 
  params, 
  responseType: 'blob' 
});
export const exportBalanceSheetExcel = (params) => axios.get(`${API}/financial-statements/balance-sheet/export-excel`, { 
  params, 
  responseType: 'blob' 
});
export const exportIncomeStatement = (params) => axios.get(`${API}/financial-statements/income-statement/export`, { 
  params, 
  responseType: 'blob' 
});
export const exportIncomeStatementPDF = (params) => axios.get(`${API}/financial-statements/income-statement/export-pdf`, { 
  params, 
  responseType: 'blob' 
});
export const exportIncomeStatementExcel = (params) => axios.get(`${API}/financial-statements/income-statement/export-excel`, { 
  params, 
  responseType: 'blob' 
});
export const getAccountAnalytics = (data) => axios.post(`${API}/financial-statements/analytics`, data);
export const getSingleAccountAnalytics = (accountId, params) => axios.get(`${API}/financial-statements/accounts/${accountId}/analytics`, { params });
export const getFlaggedAccounts = () => axios.get(`${API}/accounts/flagged`);
export const getClassificationStats = () => axios.get(`${API}/accounts/classification-stats`);

// Ad-Hoc Analysis
export const getAdHocInstances = () => axios.get(`${API}/adhoc-analysis/instances`);
export const previewColumns = (formData) => axios.post(`${API}/adhoc-analysis/preview-columns`, formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const createAdHocInstance = (formData) => axios.post(`${API}/adhoc-analysis/create-instance`, formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const getAdHocInstance = (instanceId) => axios.get(`${API}/adhoc-analysis/instances/${instanceId}`);
export const queryAdHocInstance = (instanceId, data) => axios.post(`${API}/adhoc-analysis/instances/${instanceId}/query`, data);
export const updateAdHocInstance = (instanceId, data) => axios.put(`${API}/adhoc-analysis/instances/${instanceId}`, data);
export const deleteAdHocInstance = (instanceId) => axios.delete(`${API}/adhoc-analysis/instances/${instanceId}`);
